import React, { useState, useEffect, useRef } from "react"

export default function BubbleMenu({
  wrapperRef,
  selector,
  children,
}: {
  wrapperRef: React.RefObject<HTMLDivElement | null>
  selector: string
  children: (hoverElement: HTMLElement) => React.ReactNode
}) {
  const mouseOverTimeout = useRef<number | null>(null)
  const mouseOutTimeout = useRef<number | null>(null)
  const [openAt, setOpenAt] = useState<{ x: number; y: number } | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const elementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.addEventListener("mouseover", handleMouseOver)
      wrapperRef.current.addEventListener("mouseout", handleMouseOut)
    }

    return () => {
      if (wrapperRef.current) {
        wrapperRef.current.removeEventListener("mouseover", handleMouseOver)
        wrapperRef.current.removeEventListener("mouseout", handleMouseOut)
      }
    }
  }, [wrapperRef, mouseOutTimeout, mouseOverTimeout])

  const handleMouseOver = (event: MouseEvent) => {
    const element = (event.target as any).closest(selector)
    if (!element) return
    elementRef.current = element

    if (mouseOverTimeout.current) clearTimeout(mouseOverTimeout.current)
    if (mouseOutTimeout.current) clearTimeout(mouseOutTimeout.current)

    mouseOverTimeout.current = window.setTimeout(() => {
      if (!wrapperRef.current) return

      const linkRect = element.getBoundingClientRect()
      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      let x = linkRect.x - wrapperRect.x
      if (x + 384 > wrapperRect.width) x = wrapperRect.width - 384
      let y = linkRect.y - wrapperRect.y + 25
      if (y + 50 > wrapperRect.height) y = wrapperRect.height - 100

      setOpenAt({
        x: x,
        y: y,
      })
    }, 200)
  }

  const startClearTimer = () => {
    if (mouseOutTimeout.current) clearTimeout(mouseOutTimeout.current)

    mouseOutTimeout.current = window.setTimeout(() => {
      setOpenAt(null)
    }, 500)
  }

  const handleMouseOut = (event) => {
    const element = event.target.closest(selector)
    if (element) {
      if (mouseOutTimeout.current) clearTimeout(mouseOutTimeout.current)
      startClearTimer()
    }
  }

  const cancelTimeouts = () => {
    if (mouseOverTimeout.current) clearTimeout(mouseOverTimeout.current)
    if (mouseOutTimeout.current) clearTimeout(mouseOutTimeout.current)
  }

  if (!openAt) return null

  return (
    <div
      onMouseOver={cancelTimeouts}
      onMouseOut={startClearTimer}
      className="absolute"
      // z-index has to beat the new block menu, which tippy sets at 9999
      style={{ top: `${openAt.y}px`, left: `${openAt.x}px`, zIndex: 10000 }}
    >
      {elementRef.current && children(elementRef.current)}
    </div>
  )
}
